/**
 * SOW template contract helpers
 * ─────────────────────────────
 * The SOW-flavored layer on top of the generic logical-section model in
 * `@protoss/doc`. It parses document-level settings, classifies/labels sections
 * for the workbench UI, builds a saved contract from edited settings + sections,
 * and seeds a representative `PreviewSample` for the live preview.
 */

import type {
  ContentBlockDefinition,
  DocumentChild,
  DocumentContract,
  DynamicBlockDefinition,
  ParagraphDefinition,
  SignatureBlockDefinition,
  TableCellDefinition,
  TableDefinition,
  TocDefinition,
} from '@protoss/doc';
import {
  documentChildrenToMarkdown,
  inferContentType,
  markdownToDocumentChildren,
  parseLogicalSections,
  serializeLogicalSections,
  type LogicalContentType,
  type LogicalSection,
} from '@protoss/doc';
import { slugifyHeading } from '@protoss/shared';
import {
  humanizeSlug,
  splitTokens,
  type PreviewSample,
} from '../../../components/doc/previewSample';

export {
  inferContentType,
  parseLogicalSections,
  serializeLogicalSections,
  type LogicalContentType,
  type LogicalSection,
};

const DEFAULT_MARGIN = 1440; // 1 inch in twips
const SAMPLE_DATE = 'January 15, 2026';

// ── Document settings ────────────────────────────────────────────────────────

export interface SowDocumentSettings {
  filename: string;
  title: string;
  creator: string;
  description: string;
  defaultFont: string;
  background: string;
  margins: { top: number; right: number; bottom: number; left: number };
  orientation: 'portrait' | 'landscape';
  /** The page section page-setup is read from / written to (the body page). */
  bodyPageSectionIndex: number;
}

/** The body page section is the one carrying the most content (cover pages are short). */
export function bodyPageSectionIndex(contract: DocumentContract): number {
  const sections = contract.sections ?? [];
  if (sections.length <= 1) return 0;
  let best = 0;
  let bestLen = -1;
  sections.forEach((s, i) => {
    const len = s.children?.length ?? 0;
    if (len > bestLen) {
      bestLen = len;
      best = i;
    }
  });
  return best;
}

export function parseDocumentSettings(contract: DocumentContract): SowDocumentSettings {
  const bodyIndex = bodyPageSectionIndex(contract);
  const page = contract.sections?.[bodyIndex]?.properties?.page;
  const margin = page?.margin ?? {};
  return {
    filename: contract.output?.filename ?? '',
    title: contract.document?.title ?? '',
    creator: contract.document?.creator ?? '',
    description: contract.document?.description ?? '',
    defaultFont: contract.document?.defaultFont ?? '',
    background: contract.document?.background ?? '',
    margins: {
      top: margin.top ?? DEFAULT_MARGIN,
      right: margin.right ?? DEFAULT_MARGIN,
      bottom: margin.bottom ?? DEFAULT_MARGIN,
      left: margin.left ?? DEFAULT_MARGIN,
    },
    orientation: page?.size?.orientation ?? 'portrait',
    bodyPageSectionIndex: bodyIndex,
  };
}

function setOrDelete(obj: Record<string, unknown>, key: string, value: string): void {
  if (value.trim()) obj[key] = value;
  else delete obj[key];
}

/**
 * Build the contract to persist from edited settings + sections. Re-flattens the
 * logical sections (preserving cover chrome, bindings, and unknown fields) and
 * applies document-level settings + body page setup.
 */
export function buildSowContract(
  base: DocumentContract,
  settings: SowDocumentSettings,
  sections: LogicalSection[]
): DocumentContract {
  const next = serializeLogicalSections(base, sections);

  next.output = { ...next.output, filename: settings.filename };

  const document = { ...next.document } as Record<string, string>;
  setOrDelete(document, 'title', settings.title);
  setOrDelete(document, 'creator', settings.creator);
  setOrDelete(document, 'description', settings.description);
  setOrDelete(document, 'defaultFont', settings.defaultFont);
  setOrDelete(document, 'background', settings.background);
  next.document = document;

  const bodySection = next.sections?.[settings.bodyPageSectionIndex];
  if (bodySection) {
    const properties = { ...bodySection.properties };
    const page = { ...properties.page };
    page.margin = { ...settings.margins };
    page.size = { ...page.size, orientation: settings.orientation };
    properties.page = page;
    bodySection.properties = properties;
  }

  return next;
}

// ── Content-type presentation ────────────────────────────────────────────────

export type PillTone =
  | 'neutral'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted'
  | 'accent'
  | 'ghost';

export const CONTENT_TYPE_META: Record<
  LogicalContentType,
  { label: string; tone: PillTone; description: string }
> = {
  static: {
    label: 'Static',
    tone: 'neutral',
    description: 'Fixed text copied into every document verbatim.',
  },
  ai: {
    label: 'AI-generated',
    tone: 'accent',
    description: 'Narrative written per deal by the SOW generator.',
  },
  table: {
    label: 'Data table',
    tone: 'primary',
    description: 'A table rebuilt per deal from deal data.',
  },
  signature: {
    label: 'Signature',
    tone: 'success',
    description: 'Signature lines for the signing parties.',
  },
  toc: { label: 'Contents', tone: 'muted', description: 'An auto-generated table of contents.' },
  break: { label: 'Page break', tone: 'ghost', description: 'A page break.' },
  mixed: {
    label: 'Mixed',
    tone: 'warning',
    description: 'Literal content alongside a generated block.',
  },
};

/** The content types offered in the type switcher (break/mixed are not user-selectable). */
export const SELECTABLE_CONTENT_TYPES: LogicalContentType[] = [
  'static',
  'ai',
  'table',
  'signature',
  'toc',
];

/** Compact role labels for tight spots (outline badges, the role segmented control). */
export const SHORT_CONTENT_TYPE_LABEL: Record<LogicalContentType, string> = {
  static: 'Static',
  ai: 'AI',
  table: 'Table',
  signature: 'Sign',
  toc: 'TOC',
  break: 'Break',
  mixed: 'Mixed',
};

export function sectionLabel(section: LogicalSection): string {
  if (section.headingText) return section.headingText;
  return section.isPreamble ? 'Cover / title page' : 'Untitled section';
}

// ── Block extractors (shared by the accordion + config editors) ───────────────

function firstBlock<T extends DocumentChild['type']>(
  section: LogicalSection,
  type: T
): Extract<DocumentChild, { type: T }> | undefined {
  return section.body.find((c) => c.type === type) as
    | Extract<DocumentChild, { type: T }>
    | undefined;
}

export function aiDataKey(section: LogicalSection): string | undefined {
  return firstBlock<'contentBlock'>(section, 'contentBlock')?.dataKey;
}
export function tableBlock(section: LogicalSection): DynamicBlockDefinition | undefined {
  return firstBlock<'dynamicBlock'>(section, 'dynamicBlock');
}
export function signatureBlock(section: LogicalSection): SignatureBlockDefinition | undefined {
  return firstBlock<'signatureBlock'>(section, 'signatureBlock');
}
export function tocBlock(section: LogicalSection): TocDefinition | undefined {
  return firstBlock<'toc'>(section, 'toc');
}

/** The row-scope keys a dynamicBlock's row template reads (`{{protoss:item.X}}` → X). */
export function tableColumnKeys(block: DynamicBlockDefinition): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const scanCell = (cell: TableCellDefinition) => {
    for (const child of cell.children) {
      if (child.type !== 'paragraph') continue;
      for (const run of child.children) {
        if ('type' in run) continue; // image / page-field run
        for (const seg of splitTokens(run.text ?? '')) {
          if (seg.kind === 'token' && seg.path.startsWith('item.')) {
            const key = seg.path.slice('item.'.length);
            if (!seen.has(key)) {
              seen.add(key);
              keys.push(key);
            }
          }
        }
      }
    }
  };
  block.rowTemplate.children.forEach(scanCell);
  return keys;
}

/** A short one-line summary of a section for the collapsed accordion row. */
export function sectionSummary(section: LogicalSection): string {
  switch (section.contentType) {
    case 'ai':
      return aiDataKey(section) ? `Source: ${aiDataKey(section)}` : 'AI-generated narrative';
    case 'table': {
      const block = tableBlock(section);
      const cols = block ? tableColumnKeys(block).length : 0;
      return block ? `${cols} column${cols === 1 ? '' : 's'} · ${block.dataKey}` : 'Data table';
    }
    case 'signature': {
      const parties = signatureBlock(section)?.parties.length ?? 0;
      return `${parties} part${parties === 1 ? 'y' : 'ies'}`;
    }
    case 'toc':
      return `Levels 1-${tocBlock(section)?.maxLevel ?? 3}`;
    case 'break':
      return 'Page break';
    case 'mixed':
      return 'Literal content + a generated block';
    default: {
      const text = bodyText(section.body);
      return text ? truncate(text, 90) : 'Empty section';
    }
  }
}

function bodyText(body: DocumentChild[]): string {
  const parts: string[] = [];
  for (const child of body) {
    if (child.type !== 'paragraph') continue;
    for (const run of child.children) {
      if (!('type' in run) && run.text) parts.push(run.text);
    }
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

// ── Sample data for the preview ───────────────────────────────────────────────

const SAMPLE_TOKENS: Record<string, string> = {
  clientName: 'Acme Corporation',
  clientSponsor: 'Pat Morgan',
  projectName: 'ERP Implementation',
  vendorCompany: 'Northwind Consulting',
  signatoryName: 'Jordan Lee',
  signatoryTitle: 'Managing Partner',
  documentDate: SAMPLE_DATE,
  version: '1.0',
  depositPercent: '25',
  depositAmount: '15,000',
};

const SAMPLE_LABELS = ['Discovery', 'Build & Configure', 'Total'];

function isMoneyKey(key: string): boolean {
  return /rate|amount|cost|price|total|fee|invest|budget|\$/i.test(key);
}
function isNumberKey(key: string): boolean {
  return /hour|qty|count|number|num|low|med|high|days?/i.test(key);
}

function sampleCell(key: string, row: number, isFirst: boolean): string {
  if (isMoneyKey(key)) return ['$12,000', '$36,000', '$48,000'][row] ?? '$0';
  if (isNumberKey(key)) return ['40', '120', '160'][row] ?? '0';
  if (isFirst || /area|role|name|title|item|service|milestone|task|description|phase/i.test(key)) {
    return SAMPLE_LABELS[row] ?? 'Sample';
  }
  return ['Sample A', 'Sample B', 'Total'][row] ?? 'Sample';
}

function sampleNarrative(dataKey: string): string {
  const topic = humanizeSlug(dataKey.split('.').pop() ?? dataKey).toLowerCase();
  return [
    `This section outlines the ${topic} for the engagement, including the key activities, responsibilities, and deliverables agreed by both parties.`,
    '',
    `- A representative ${topic} item`,
    `- Another ${topic} consideration`,
  ].join('\n');
}

/** Walk every page section's children, visiting each block. */
function eachBlock(contract: DocumentContract, visit: (child: DocumentChild) => void): void {
  for (const section of contract.sections ?? []) {
    for (const child of section.children ?? []) visit(child);
  }
}

/** Seed a believable `PreviewSample` from the contract's bindings and blocks. */
export function defaultPreviewSample(contract: DocumentContract): PreviewSample {
  const tokens: PreviewSample['tokens'] = { 'date.today': SAMPLE_DATE };
  for (const key of Object.keys(contract.bindings?.context ?? {})) {
    tokens[`context.${key}`] = SAMPLE_TOKENS[key] ?? humanizeSlug(key);
  }

  const data: PreviewSample['data'] = {};
  const narrative: PreviewSample['narrative'] = {};
  eachBlock(contract, (child) => {
    if (child.type === 'dynamicBlock') {
      const keys = tableColumnKeys(child);
      data[child.dataKey] = [0, 1, 2].map((row) => {
        const out: Record<string, string> = {};
        keys.forEach((key, i) => (out[key] = sampleCell(key, row, i === 0)));
        return out;
      });
    } else if (child.type === 'contentBlock') {
      narrative[child.dataKey] = sampleNarrative(child.dataKey);
    }
  });

  return { version: '1.0', tokens, data, narrative };
}

// ── Edit transforms ──────────────────────────────────────────────────────────
// Pure (sections, sample) → (sections, sample) operations the workbench applies
// in edit mode. They keep the contract, the AI dataKeys and the preview sample in
// lockstep so a rename or type switch never orphans a narrative slot.

function newSectionId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  return `sec-${c?.randomUUID ? c.randomUUID() : Math.random().toString(36).slice(2)}`;
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function cloneSection(section: LogicalSection): LogicalSection {
  return {
    ...section,
    heading: section.heading ? deepClone(section.heading) : null,
    body: deepClone(section.body),
  };
}

function makeHeading(text: string, level = 1): ParagraphDefinition {
  const clamped = Math.min(6, Math.max(1, level));
  return {
    type: 'paragraph',
    heading: `Heading${clamped}` as ParagraphDefinition['heading'],
    children: [{ text }],
  };
}

export function aiDataKeyFor(headingText: string): string {
  return `narrative.${slugifyHeading(headingText) || 'section'}`;
}
export function tableDataKeyFor(headingText: string): string {
  return `${slugifyHeading(headingText) || 'data'}_table`;
}

function simpleCell(text: string): TableCellDefinition {
  return { children: [{ type: 'paragraph', children: [{ text }] }] };
}

/** A minimal, structurally-valid 2-column data table the author can extend. */
export function buildBasicDynamicBlock(dataKey: string): DynamicBlockDefinition {
  return {
    type: 'dynamicBlock',
    dataKey,
    columnWidths: [4680, 4680],
    headerRows: [{ tableHeader: true, children: [simpleCell('Column 1'), simpleCell('Column 2')] }],
    rowTemplate: {
      children: [simpleCell('{{protoss:item.column_1}}'), simpleCell('{{protoss:item.column_2}}')],
    },
  };
}

function defaultBodyForType(type: LogicalContentType, headingText: string): DocumentChild[] {
  switch (type) {
    case 'ai':
      return [{ type: 'contentBlock', dataKey: aiDataKeyFor(headingText) }];
    case 'table':
      return [buildBasicDynamicBlock(tableDataKeyFor(headingText))];
    case 'signature':
      return [
        {
          type: 'signatureBlock',
          mode: 'visual',
          parties: [{ label: 'Client', fields: ['name', 'title', 'signature', 'date'] }],
        },
      ];
    case 'toc':
      return [{ type: 'toc', maxLevel: 3 }];
    default:
      return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
}

function defaultHeadingText(type: LogicalContentType): string {
  switch (type) {
    case 'table':
      return 'New table';
    case 'signature':
      return 'Signatures';
    case 'toc':
      return 'Contents';
    default:
      return 'New section';
  }
}

function defaultTableRows(keys: string[]): Array<Record<string, string>> {
  return [0, 1, 2].map((row) => {
    const out: Record<string, string> = {};
    keys.forEach((key, i) => (out[key] = sampleCell(key, row, i === 0)));
    return out;
  });
}

function remapNarrative(sample: PreviewSample, oldKey: string, newKey: string): PreviewSample {
  if (oldKey === newKey) return sample;
  const narrative = { ...sample.narrative };
  if (oldKey in narrative) {
    narrative[newKey] = narrative[oldKey]!;
    delete narrative[oldKey];
  }
  return { ...sample, narrative };
}

/** Set a section's heading text, cascading the AI contentBlock dataKey + sample key. */
export function renameSection(
  sections: LogicalSection[],
  sample: PreviewSample,
  id: string,
  text: string
): { sections: LogicalSection[]; sample: PreviewSample } {
  let nextSample = sample;
  const nextSections = sections.map((section) => {
    if (section.id !== id) return section;
    const next = cloneSection(section);
    if (next.heading) next.heading.children = [{ text }];
    else if (!next.isPreamble) {
      next.heading = makeHeading(text, next.headingLevel ?? 1);
      next.headingLevel = next.headingLevel ?? 1;
    }
    next.headingText = text;
    if (next.contentType === 'ai') {
      const cb = next.body.find((c): c is ContentBlockDefinition => c.type === 'contentBlock');
      if (cb) {
        const newKey = aiDataKeyFor(text);
        if (cb.dataKey !== newKey) {
          nextSample = remapNarrative(nextSample, cb.dataKey, newKey);
          cb.dataKey = newKey;
        }
      }
    }
    return next;
  });
  return { sections: nextSections, sample: nextSample };
}

function simpleParagraph(text: string): ParagraphDefinition {
  return { type: 'paragraph', children: [{ text }] };
}

function literalCell(text: string): TableCellDefinition {
  return { children: [simpleParagraph(text)] };
}

function cellPlainText(cell: TableCellDefinition): string {
  for (const child of cell.children) {
    if (child.type !== 'paragraph') continue;
    const run = child.children.find((r) => !('type' in r));
    if (run && !('type' in run) && run.text) return run.text;
  }
  return '';
}

/** A dynamicBlock rendered to a literal table from the sample rows (for static conversion). */
function dynamicBlockToLiteralTable(
  block: DynamicBlockDefinition,
  sample: PreviewSample
): TableDefinition {
  const keys = tableColumnKeys(block);
  const headerCells = block.headerRows?.[0]?.children;
  const labels =
    headerCells && headerCells.length === keys.length
      ? headerCells.map(cellPlainText)
      : keys.map((k) => humanizeSlug(k));
  return {
    type: 'table',
    width: { size: 100, type: 'pct' },
    rows: [
      { tableHeader: true, children: labels.map(literalCell) },
      ...(sample.data[block.dataKey] ?? []).map((row) => ({
        children: keys.map((k) => literalCell(row[k] ?? '')),
      })),
    ],
  };
}

/**
 * Build literal, editable content from a section's current body so "Static" carries
 * the actual content (the example's data / the sample narrative / the sample table) -
 * never an empty box or a token. Reuses the preview sample so what the author saw is
 * what they get to edit.
 */
function staticBodyFromSection(section: LogicalSection, sample: PreviewSample): DocumentChild[] {
  const out: DocumentChild[] = [];
  for (const child of section.body) {
    if (child.type === 'paragraph' || child.type === 'table') {
      out.push(child);
    } else if (child.type === 'contentBlock') {
      out.push(...markdownToDocumentChildren(sample.narrative[child.dataKey] ?? ''));
    } else if (child.type === 'dynamicBlock') {
      out.push(dynamicBlockToLiteralTable(child, sample));
    }
    // signatureBlock / toc / pageBreak are dropped when a section becomes static prose.
  }
  return out.length > 0 ? out : [simpleParagraph('')];
}

/** Replace a section's content type, swapping its body and seeding sample data. */
export function switchSectionType(
  sections: LogicalSection[],
  sample: PreviewSample,
  id: string,
  type: LogicalContentType
): { sections: LogicalSection[]; sample: PreviewSample } {
  let nextSample = sample;
  const nextSections = sections.map((section) => {
    if (section.id !== id) return section;
    const next = cloneSection(section);
    // Static carries the actual content (converted from whatever it was); other types
    // start from their default skeleton.
    next.body =
      type === 'static'
        ? staticBodyFromSection(section, sample)
        : defaultBodyForType(type, next.headingText);
    next.contentType = type;
    if (type === 'ai') {
      const key = aiDataKeyFor(next.headingText);
      if (!nextSample.narrative[key]) {
        nextSample = {
          ...nextSample,
          narrative: { ...nextSample.narrative, [key]: sampleNarrative(key) },
        };
      }
    } else if (type === 'table') {
      const key = tableDataKeyFor(next.headingText);
      if (!nextSample.data[key]) {
        nextSample = {
          ...nextSample,
          data: { ...nextSample.data, [key]: defaultTableRows(['column_1', 'column_2']) },
        };
      }
    }
    return next;
  });
  return { sections: nextSections, sample: nextSample };
}

/** Insert a new section (after `afterId`, else at the end) and seed its sample. */
export function addSection(
  sections: LogicalSection[],
  sample: PreviewSample,
  type: LogicalContentType,
  afterId?: string
): { sections: LogicalSection[]; sample: PreviewSample } {
  const idx = afterId ? sections.findIndex((s) => s.id === afterId) : sections.length - 1;
  const ref = sections[idx] ?? sections[sections.length - 1];
  const heading = defaultHeadingText(type);
  const section: LogicalSection = {
    id: newSectionId(),
    pageSectionIndex: ref?.pageSectionIndex ?? 0,
    isPreamble: false,
    heading: makeHeading(heading, 1),
    headingText: heading,
    headingLevel: 1,
    contentType: type,
    body: defaultBodyForType(type, heading),
  };
  let nextSample = sample;
  if (type === 'ai') {
    const key = aiDataKeyFor(heading);
    nextSample = { ...sample, narrative: { ...sample.narrative, [key]: sampleNarrative(key) } };
  } else if (type === 'table') {
    const key = tableDataKeyFor(heading);
    nextSample = { ...sample, data: { ...sample.data, [key]: defaultTableRows(['column_1', 'column_2']) } };
  }
  const next = [...sections];
  next.splice(idx + 1, 0, section);
  return { sections: next, sample: nextSample };
}

export function removeSection(sections: LogicalSection[], id: string): LogicalSection[] {
  return sections.filter((s) => s.id !== id);
}

/** Move a section up/down, but only within its own page section. */
export function moveSection(
  sections: LogicalSection[],
  id: string,
  dir: 'up' | 'down'
): LogicalSection[] {
  const idx = sections.findIndex((s) => s.id === id);
  if (idx < 0) return sections;
  const target = dir === 'up' ? idx - 1 : idx + 1;
  const a = sections[idx];
  const b = sections[target];
  if (!a || !b || a.pageSectionIndex !== b.pageSectionIndex) return sections;
  const next = [...sections];
  next[idx] = b;
  next[target] = a;
  return next;
}

/** Move a section to an arbitrary index (drag-drop), only within its page section. */
export function reorderSection(
  sections: LogicalSection[],
  id: string,
  toIndex: number
): LogicalSection[] {
  const from = sections.findIndex((s) => s.id === id);
  if (from < 0 || toIndex < 0 || toIndex >= sections.length || from === toIndex) return sections;
  const moving = sections[from]!;
  const target = sections[toIndex]!;
  if (moving.pageSectionIndex !== target.pageSectionIndex) return sections;
  const next = [...sections];
  next.splice(from, 1);
  next.splice(toIndex, 0, moving);
  return next;
}

export function duplicateSection(
  sections: LogicalSection[],
  sample: PreviewSample,
  id: string
): { sections: LogicalSection[]; sample: PreviewSample } {
  const idx = sections.findIndex((s) => s.id === id);
  if (idx < 0) return { sections, sample };
  const source = sections[idx]!;
  const copy = cloneSection(source);
  copy.id = newSectionId();
  const headingText = source.headingText ? `${source.headingText} (copy)` : 'Section (copy)';
  copy.headingText = headingText;
  if (copy.heading) copy.heading.children = [{ text: headingText }];
  let nextSample = sample;
  if (copy.contentType === 'ai') {
    const cb = copy.body.find((c): c is ContentBlockDefinition => c.type === 'contentBlock');
    if (cb) {
      const newKey = aiDataKeyFor(headingText);
      nextSample = { ...sample, narrative: { ...sample.narrative, [newKey]: sample.narrative[cb.dataKey] ?? sampleNarrative(newKey) } };
      cb.dataKey = newKey;
    }
  }
  const next = [...sections];
  next.splice(idx + 1, 0, copy);
  return { sections: next, sample: nextSample };
}

/** Serialize a static section's literal body to markdown (for the rich-text editor). */
export function sectionBodyToMarkdown(section: LogicalSection): string {
  return documentChildrenToMarkdown(section.body);
}

/** Replace a section's body from edited markdown (static content). */
export function withStaticBody(section: LogicalSection, markdown: string): LogicalSection {
  return withBody(section, markdownToDocumentChildren(markdown));
}

/** Replace a section's body with new children (re-inferring its content type). */
export function withBody(section: LogicalSection, body: DocumentChild[]): LogicalSection {
  const next = cloneSection(section);
  next.body = body;
  next.contentType = inferContentType(body);
  return next;
}

/** Clone a section and mutate its first block of `type` in place (config edits). */
export function updateSectionBlock<T extends DocumentChild['type']>(
  section: LogicalSection,
  type: T,
  updater: (block: Extract<DocumentChild, { type: T }>) => void
): LogicalSection {
  const next = cloneSection(section);
  const block = next.body.find((c) => c.type === type) as
    | Extract<DocumentChild, { type: T }>
    | undefined;
  if (block) updater(block);
  return next;
}

// Re-export block types the workbench components need to type their props.
export type {
  ContentBlockDefinition,
  DynamicBlockDefinition,
  ParagraphDefinition,
  SignatureBlockDefinition,
  TocDefinition,
};
